/**
 * A picture in a format the press step can put in a PDF.
 *
 * The press file embeds JPEG and PNG and nothing else. A WebP or GIF shows in
 * the designer perfectly well and then goes missing from the press file, which
 * is the worst way to find out. So the browser, which can read both, turns them
 * into a PNG (when any pixel is see-through) or a high-quality JPEG before the
 * picture is uploaded or kept. The server also converts older uploads when the
 * press files are made (Trimbox_Assets::printable_path).
 */

const PRINTABLE = [ 'image/jpeg', 'image/png' ];

export async function printableFile( file: File ): Promise<File> {
  if ( PRINTABLE.includes( file.type ) || ! file.type.startsWith( 'image/' ) ) return file;

  let bitmap: ImageBitmap;

  try {
    bitmap = await createImageBitmap( file );
  } catch {
    // Not readable here either; the upload's own checks will say so.
    return file;
  }

  const canvas = document.createElement( 'canvas' );

  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext( '2d' );

  if ( ! ctx ) return file;

  ctx.drawImage( bitmap, 0, 0 );
  bitmap.close();

  // See-through anywhere means PNG, so a logo keeps its transparent background.
  const pixels = ctx.getImageData( 0, 0, canvas.width, canvas.height ).data;
  let clear = false;

  for ( let i = 3; i < pixels.length; i += 4 ) {
    if ( ( pixels[ i ] as number ) < 255 ) {
      clear = true;
      break;
    }
  }

  const type = clear ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob | null>( ( done ) => canvas.toBlob( done, type, 0.95 ) );

  if ( ! blob ) return file;

  const name = file.name.replace( /\.[a-z0-9]+$/i, '' ) + ( clear ? '.png' : '.jpg' );

  return new File( [ blob ], name, { type } );
}
